import styles from './ArticleRenderer.module.css';

interface ParagraphBlock {
  type: 'paragraph';
  text: string;
}

interface HeadingBlock {
  type: 'heading';
  level: 2 | 3;
  text: string;
}

interface ListBlock {
  type: 'list';
  ordered: boolean;
  items: string[];
}

interface ImageBlock {
  type: 'image';
  src: string;
  alt: string;
  caption?: string;
  width?: number;
  height?: number;
}

interface QuoteBlock {
  type: 'quote';
  text: string;
  author?: string;
}

interface CodeBlock {
  type: 'code';
  language: string;
  content: string;
}

export type ContentBlock =
  | ParagraphBlock
  | HeadingBlock
  | ListBlock
  | ImageBlock
  | QuoteBlock
  | CodeBlock;

interface ArticleRendererProps {
  body: ContentBlock[];
}

export function ArticleRenderer({ body }: ArticleRendererProps) {
  return (
    <div className={styles.prose}>
      {body.map((block, i) => {
        switch (block.type) {
          case 'paragraph':
            return <p key={i} className={styles.paragraph}>{block.text}</p>;

          case 'heading':
            return block.level === 2
              ? <h2 key={i} className={styles.h2}>{block.text}</h2>
              : <h3 key={i} className={styles.h3}>{block.text}</h3>;

          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={i} className={block.ordered ? styles.ol : styles.ul}>
                {block.items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </Tag>
            );
          }

          case 'image':
            return (
              <figure key={i} className={styles.figure}>
                <img
                  src={block.src}
                  alt={block.alt}
                  loading="lazy"
                  width={block.width ?? 1200}
                  height={block.height ?? 800}
                />
                {block.caption && (
                  <figcaption className={styles.caption}>{block.caption}</figcaption>
                )}
              </figure>
            );

          case 'quote':
            return (
              <blockquote key={i} className={styles.blockquote}>
                <p>{block.text}</p>
                {block.author && (
                  <footer className={styles.quoteAuthor}>— {block.author}</footer>
                )}
              </blockquote>
            );

          case 'code':
            return (
              <pre key={i} className={styles.codeBlock}>
                <code>{block.content}</code>
              </pre>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
